import React from "react";
import Marquee from "react-fast-marquee";
import { Star } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { GOOGLE_REVIEWS, GOOGLE_RATING, GOOGLE_REVIEW_COUNT } from "@/data/reviews";

export function GoogleG({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.69 28.18c-.44-1.32-.69-2.73-.69-4.18s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z" />
      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
    </svg>
  );
}

function Stars({ n }) {
  return (
    <span className="flex gap-0.5 text-[#FBBC05]">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={15} fill={i < n ? "currentColor" : "none"} className={i < n ? "" : "text-ink/20"} />
      ))}
    </span>
  );
}

// Compact trust bar (bandeau) — à placer en évidence près du haut de page.
export function GoogleTrustBar({ dark = false }) {
  return (
    <Reveal>
      <div
        data-testid="google-trust-bar"
        className={`flex flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-2xl border px-6 py-4 ${
          dark ? "border-white/15 bg-white/5" : "border-ink/10 bg-white shadow-sm"
        }`}
      >
        <GoogleG size={26} />
        <span className={`text-3xl font-extrabold ${dark ? "text-white" : "text-ink"}`}>{GOOGLE_RATING}</span>
        <Stars n={5} />
        <span className={`text-sm font-semibold ${dark ? "text-white/70" : "text-ink/60"}`}>
          {GOOGLE_REVIEW_COUNT} avis Google vérifiés
        </span>
      </div>
    </Reveal>
  );
}

function ReviewCard({ r }) {
  return (
    <div className="mx-3 w-[330px] shrink-0 rounded-2xl border border-ink/10 bg-white p-6">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-full text-white font-bold" style={{ background: r.color }}>
          {r.initial}
        </span>
        <div className="flex-1">
          <p className="text-sm font-bold text-ink leading-tight">{r.name}</p>
          <p className="text-xs text-ink/45">{r.when}</p>
        </div>
        <GoogleG size={18} />
      </div>
      <div className="mt-3"><Stars n={r.rating} /></div>
      <p className="mt-3 text-sm text-ink/70 leading-relaxed line-clamp-4">{r.text}</p>
    </div>
  );
}

// Section complète d'avis Google (bannière + défilement).
export default function GoogleReviews() {
  return (
    <section data-testid="google-reviews" className="bg-mist py-20 sm:py-24 overflow-hidden">
      <div className="container-x">
        <Reveal className="flex flex-col items-center text-center">
          <div className="flex items-center gap-3">
            <GoogleG size={34} />
            <span className="text-xl font-bold text-ink">Avis Google</span>
          </div>
          <div className="mt-5 flex items-center gap-4">
            <span className="text-6xl font-extrabold text-ink leading-none">{GOOGLE_RATING}</span>
            <div className="text-left">
              <Stars n={5} />
              <p className="mt-1 text-sm font-semibold text-ink/60">Basé sur {GOOGLE_REVIEW_COUNT} avis clients</p>
            </div>
          </div>
          <h2 className="mt-6 text-3xl sm:text-4xl font-bold tracking-tight text-ink max-w-2xl">
            Ils recommandent FuiteX les yeux fermés
          </h2>
        </Reveal>
      </div>

      <div className="mt-12">
        <Marquee speed={40} gradient gradientColor="#E6E8EB" gradientWidth={80} pauseOnHover>
          {GOOGLE_REVIEWS.map((r, i) => <ReviewCard key={i} r={r} />)}
        </Marquee>
      </div>
    </section>
  );
}
